import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { CaseIdSchema } from '@casetrace/shared';
import { collection, onSnapshot } from 'firebase/firestore';
import { db } from '../lib/firebase';

/**
 * Refresher tidslinjen automatisk når en importjobb fullføres.
 * useCaseEvents er en engangs-paginert query (ikke onSnapshot), så uten dette
 * må brukeren refreshe siden for å se nyimporterte hendelser.
 */
export function useRefreshEventsOnImport(caseId: string): void {
  const queryClient = useQueryClient();
  const jobStatusByIdRef = useRef(new Map<string, string>());
  const delayedRefreshTimersRef = useRef<number[]>([]);

  function clearDelayedRefreshTimers() {
    for (const timerId of delayedRefreshTimersRef.current) {
      window.clearTimeout(timerId);
    }
    delayedRefreshTimersRef.current = [];
  }

  function refreshCaseRoomQueries() {
    void queryClient.invalidateQueries({ queryKey: ['events', caseId] });
    void queryClient.invalidateQueries({ queryKey: ['event-type-counts', caseId] });
    void queryClient.invalidateQueries({ queryKey: ['timeline-search', caseId] });
  }

  useEffect(() => {
    if (!caseId) return;
    const parsedCaseId = CaseIdSchema.parse(caseId);
    const ref = collection(db, 'cases', parsedCaseId, 'importJobs');
    const unsubscribe = onSnapshot(ref, (snap) => {
      const nextJobStatusById = new Map<string, string>();
      let hasNewlyCompletedJob = false;

      for (const doc of snap.docs) {
        const status = (doc.data() as { status?: string }).status;
        if (typeof status !== 'string') continue;
        nextJobStatusById.set(doc.id, status);

        const previousStatus = jobStatusByIdRef.current.get(doc.id);
        if (status === 'completed' && previousStatus !== 'completed') {
          hasNewlyCompletedJob = true;
        }
      }

      jobStatusByIdRef.current = nextJobStatusById;

      if (hasNewlyCompletedJob) {
        refreshCaseRoomQueries();
        // Ekstra refresh for å fange etterslep i backend/indexering på tvers av kilder.
        clearDelayedRefreshTimers();
        delayedRefreshTimersRef.current = [1200, 5000].map((delayMs) =>
          window.setTimeout(() => {
            refreshCaseRoomQueries();
          }, delayMs),
        );
      }
    });

    return () => {
      unsubscribe();
      clearDelayedRefreshTimers();
    };
  }, [caseId, queryClient]);

  useEffect(
    () => () => {
      clearDelayedRefreshTimers();
    },
    [],
  );
}
